"use client";

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AssetsCombobox, AssetsProvider } from '@/components/ui/AssetsCombobox';

// Match the Asset type expected by AssetsCombobox
interface Asset {
  id: string;
  name: string;
  type?: string;
  description?: string;
  originalAsset: any; // Original asset data from the service
}

export function AssetsComboboxExample() {
  // State for the main example
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  
  // State for the custom assets example
  const [customSelectedAssets, setCustomSelectedAssets] = useState<string[]>([]);
  
  // Custom assets data
  const customAssets: Asset[] = [
    { 
      id: 'custom-1', 
      name: 'Custom Server', 
      type: 'Server', 
      description: 'Primary application server',
      originalAsset: { id: 'custom-1', name: 'Custom Server', type: 'Server', description: 'Primary application server' }
    },
    { 
      id: 'custom-2', 
      name: 'Backup Database', 
      type: 'Database', 
      description: 'Secondary backup database',
      originalAsset: { id: 'custom-2', name: 'Backup Database', type: 'Database', description: 'Secondary backup database' }
    },
    { 
      id: 'custom-3', 
      name: 'Load Balancer', 
      type: 'Network', 
      description: 'Traffic distribution',
      originalAsset: { id: 'custom-3', name: 'Load Balancer', type: 'Network', description: 'Traffic distribution' }
    },
    { 
      id: 'custom-4', 
      name: 'File Storage', 
      type: 'Storage', 
      description: 'User file storage',
      originalAsset: { id: 'custom-4', name: 'File Storage', type: 'Storage', description: 'User file storage' }
    },
    { 
      id: 'custom-5', 
      name: 'API Gateway', 
      type: 'API', 
      description: 'External API access point',
      originalAsset: { id: 'custom-5', name: 'API Gateway', type: 'API', description: 'External API access point' }
    },
  ];
  
  // Event handlers
  const handleAssetSelect = (assetId: string) => {
    console.log('Asset selected:', assetId);
  };
  
  const handleAssetDeselect = (assetId: string) => {
    console.log('Asset deselected:', assetId);
  };
  
  const handleAllSelect = () => {
    console.log('All assets selected');
  };
  
  const handleAllDeselect = () => {
    console.log('All assets deselected');
  };
  
  // Get asset display name by ID
  const getAssetName = (id: string): string => {
    const asset = customAssets.find(a => a.id === id);
    return asset ? asset.name : `Asset (${id})`;
  };
  
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Assets Combobox</h1>
          <p className="text-muted-foreground">
            A searchable, filterable, and multi-select combobox for assets.
          </p>
        </div>
        
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Usage</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Default with Provider</Label>
                <p className="text-sm text-muted-foreground">
                  Basic usage with the AssetsProvider for automatic data fetching
                </p>
                
                <div className="max-w-xl">
                  <AssetsProvider>
                    <AssetsCombobox
                      selected={selectedAssets}
                      setSelected={setSelectedAssets}
                      placeholder="Search assets..."
                      onAssetSelect={handleAssetSelect}
                      onAssetDeselect={handleAssetDeselect}
                      onAllSelect={handleAllSelect}
                      onAllDeselect={handleAllDeselect}
                    />
                  </AssetsProvider>
                </div>
                
                {selectedAssets.length > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">Selected Assets ({selectedAssets.length})</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedAssets([])}
                        className="h-7 text-xs"
                      >
                        Clear all
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedAssets.map(assetId => (
                        <Badge
                          key={assetId}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          {getAssetName(assetId)}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedAssets(prev => prev.filter(id => id !== assetId));
                            }}
                            className="opacity-50 hover:opacity-100"
                          >
                            <span className="sr-only">Remove</span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="h-3 w-3"
                            >
                              <path d="M18 6L6 18" />
                              <path d="M6 6l12 12" />
                            </svg>
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Custom Assets Example</Label>
                <p className="text-sm text-muted-foreground">
                  Example with a custom list of assets passed directly to the component
                </p>
                
                <div className="max-w-xl">
                  <AssetsCombobox
                    selected={customSelectedAssets}
                    setSelected={setCustomSelectedAssets}
                    assets={customAssets}
                    placeholder="Search custom assets..."
                  />
                </div>
                
                {customSelectedAssets.length > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">Selected Custom Assets ({customSelectedAssets.length})</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCustomSelectedAssets([])}
                        className="h-7 text-xs"
                      >
                        Clear all
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {customSelectedAssets.map(assetId => {
                        const asset = customAssets.find(a => a.id === assetId);
                        if (!asset) return null;
                        
                        return (
                          <Badge
                            key={assetId}
                            variant="secondary"
                            className="flex items-center gap-1"
                          >
                            {asset.name}
                            {asset.type && (
                              <span className="ml-1 text-xs opacity-70">({asset.type})</span>
                            )}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCustomSelectedAssets(prev => prev.filter(id => id !== assetId));
                              }}
                              className="opacity-50 hover:opacity-100"
                            >
                              <span className="sr-only">Remove</span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="h-3 w-3"
                              >
                                <path d="M18 6L6 18" />
                                <path d="M6 6l12 12" />
                              </svg>
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Example Code</Label>
                <pre className="bg-muted p-4 rounded-md text-sm overflow-x-auto">
                  <code>
{`// Basic usage with context provider
<AssetsProvider>
  <AssetsCombobox
    selected={selectedAssets}
    setSelected={setSelectedAssets}
    placeholder="Search and select assets..."
  />
</AssetsProvider>

// With custom assets array
<AssetsCombobox
  selected={selectedAssets}
  setSelected={setSelectedAssets}
  assets={customAssets}
  placeholder="Search custom assets..."
  onAssetSelect={(assetId) => console.log('Selected:', assetId)}
  onAssetDeselect={(assetId) => console.log('Deselected:', assetId)}
  onAllSelect={() => console.log('All selected')}
  onAllDeselect={() => console.log('All deselected')}
  className="w-full"
  fullWidth
/>`}
                  </code>
                </pre>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Features</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Search and filter assets by name or description</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Filter by asset type with clickable type chips</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Multi-select with badges for selected items</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Keyboard navigation and accessibility support</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Customizable with props for selected values, callbacks, and styling</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Works with or without the AssetsProvider context</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default AssetsComboboxExample;
